import { InputNumberLocalized } from '@components'

import {
  ComboBox,
  Divider,
  Flex,
  FormItemsGroup,
  IconButton,
  NavItem,
  Select,
  Space,
  Tooltip,
} from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'
import { InvoiceFormRowLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormRowLine'
import { calculateRowLineTotals } from '@features/invoices/utils/calculateRowLineTotals'
import { ProductTypeToggleGroup } from '@features/products/elements/ProductTypeToggleGroup'

import { useFormContext, useWatch } from '../../../../hooks'
import { DEMO_INVOICE_PRODUCTS } from '../../constants/demoInvoiceProducts'
import { demoInvoiceUnits } from '../../constants/demoInvoiceUnits'
import { DEMO_INVOICE_VAT_RATES } from '../../constants/demoInvoiceVatRates'
import { DemoInvoiceUnit } from '../../enums/demoInvoiceUnit'
import { useDemoInvoiceLocale } from '../../hooks/useDemoInvoiceLocale'
import { useTrackDemoInvoiceEditor } from '../../hooks/useTrackDemoInvoiceEditor'
import { DemoInvoiceFormSchema } from '../../types/demoInvoiceFormSchema'
import { getDemoInvoiceLineColumnsTemplate } from '../../utils/getDemoInvoiceLineColumnsTemplate'
import { DemoInvoiceLineDiscount } from '../DemoInvoiceLineDiscount'

interface DemoInvoiceLineProps {
  discountMode: InvoiceDiscountMode
  isRemovable: boolean
  lineIndex: number
  onRemove: (lineIndex: number) => void
}

export const DemoInvoiceLine = ({
  discountMode,
  isRemovable,
  lineIndex,
  onRemove,
}: DemoInvoiceLineProps): ReactElement => {
  const { t } = useTranslation()
  const locale = useDemoInvoiceLocale()
  const { trackDemoInvoiceEditor } = useTrackDemoInvoiceEditor()
  const { control, FormItem, setValue } = useFormContext<DemoInvoiceFormSchema>()
  const currency = useWatch({ control, name: 'currency' })
  const discountType = useWatch({ control, name: 'discountType' })
  const line = useWatch({ control, name: `invoiceLines.${lineIndex}` }) as InvoiceFormRowLine

  const productItems: NavItem<string>[] = useMemo(
    () =>
      DEMO_INVOICE_PRODUCTS.map((product) => ({ children: t(product.tName), id: product.id, value: t(product.tName) })),
    [t],
  )

  const unitItems: NavItem<DemoInvoiceUnit>[] = useMemo(
    () => demoInvoiceUnits.map((unit) => ({ children: unit, id: unit, value: unit })),
    [],
  )

  const vatRateItems: NavItem<number>[] = useMemo(
    () =>
      DEMO_INVOICE_VAT_RATES.map((vatRate) => ({
        children: new Intl.NumberFormat(locale.replace('_', '-'), {
          maximumFractionDigits: 1,
          style: 'percent',
        }).format(vatRate),
        id: String(vatRate),
        value: vatRate,
      })),
    [locale],
  )

  const columnsTemplate = getDemoInvoiceLineColumnsTemplate(discountMode)
  const { totalIncludingVat } = calculateRowLineTotals({ currency, discountType, line })

  const handleProductNameChange = useCallback(
    (productName: string) => {
      const product = DEMO_INVOICE_PRODUCTS.find((demoProduct) => t(demoProduct.tName) === productName)

      if (!product) {
        return
      }

      setValue(`invoiceLines.${lineIndex}.productName`, productName)
      setValue(`invoiceLines.${lineIndex}.productType`, product.productType)
      setValue(`invoiceLines.${lineIndex}.unit`, product.unit)
      setValue(`invoiceLines.${lineIndex}.unitPrice`, product.unitPrice)
      setValue(`invoiceLines.${lineIndex}.vatRatePercent`, product.vatRatePercent)
    },
    [lineIndex, setValue, t],
  )

  const handleProductNameFocus = useCallback(() => {
    trackDemoInvoiceEditor('Form - Invoice lines - Items dropdown opened')
  }, [trackDemoInvoiceEditor])

  const handleRemoveClick = useCallback(() => {
    onRemove(lineIndex)
  }, [lineIndex, onRemove])

  return (
    <>
      <Divider />
      <Space size="s8" />
      <Flex alignContent="center">
        <FormItemsGroup itemsInRow={columnsTemplate.length} itemsTemplate={columnsTemplate}>
          <FormItem
            name={`invoiceLines.${lineIndex}.productName`}
            render={({ field: { name, value } }) => (
              <ComboBox<string>
                aria-label={t('external_invoices.editor.form.field.line.header.name')}
                initialInputValue={value}
                isMultiselect={false}
                items={productItems}
                name={name}
                onChange={handleProductNameChange}
                onFocus={handleProductNameFocus}
                onInputChange={(event) => setValue(`invoiceLines.${lineIndex}.productName`, event.target.value)}
                shouldDisplayItemValue={false}
                size="sm"
              />
            )}
          />
          <FormItem
            name={`invoiceLines.${lineIndex}.productType`}
            render={({ field: { onChange, value } }) => <ProductTypeToggleGroup onChange={onChange} value={value} />}
          />
          <FormItem
            name={`invoiceLines.${lineIndex}.quantity`}
            render={({ field }) => (
              <InputNumberLocalized
                {...field}
                aria-label={t('external_invoices.editor.form.field.line.header.quantity')}
                keyboard
                locale={locale}
                min={0}
                size="sm"
                withCalculations={false}
              />
            )}
          />
          <FormItem
            name={`invoiceLines.${lineIndex}.unit`}
            render={({ field: { value, onChange, ...rest } }) => (
              <Select<DemoInvoiceUnit>
                {...rest}
                dropdownSize="fitTrigger"
                items={unitItems}
                onSelect={(_id, unit) => onChange(unit)}
                selectLook={false}
                selectedId={value}
                size="sm"
                withSearch={false}
              />
            )}
          />
          <FormItem
            name={`invoiceLines.${lineIndex}.unitPrice`}
            render={({ field }) => (
              <InputNumberLocalized
                {...field}
                aria-label={t('external_invoices.editor.form.field.line.header.unit_price')}
                locale={locale}
                min={0}
                size="sm"
                suffix={currency}
              />
            )}
          />
          {discountMode === InvoiceDiscountMode.ByItem && <DemoInvoiceLineDiscount lineIndex={lineIndex} />}
          <FormItem
            name={`invoiceLines.${lineIndex}.vatRatePercent`}
            render={({ field: { value, onChange, ...rest } }) => (
              <Select<number>
                {...rest}
                dropdownSize="fitTrigger"
                items={vatRateItems}
                onSelect={(_id, vatRate) => onChange(vatRate)}
                selectLook={false}
                selectedId={value === undefined ? undefined : String(value)}
                size="sm"
                withSearch={false}
              />
            )}
          />
          <FormItem
            name={`invoiceLines.${lineIndex}.totalIncludingVat`}
            render={({ field: { name } }) => (
              <InputNumberLocalized
                aria-label={t('external_invoices.editor.form.field.line.header.total')}
                locale={locale}
                name={name}
                readOnly
                size="sm"
                suffix={currency}
                value={totalIncludingVat}
              />
            )}
          />
        </FormItemsGroup>
        <Space horizontal size="s8" />
        <Tooltip label={t('remove')} placement="top">
          <IconButton
            aria-label={t('remove')}
            disabled={!isRemovable}
            icon="trash"
            onClick={handleRemoveClick}
            size="md"
          />
        </Tooltip>
      </Flex>
    </>
  )
}
